import { useEffect, useRef, useState } from 'react';
import { useScrollPosition } from '@/hooks/useScrollPosition';

function useInView(threshold = 0.1) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setInView(true);
    }, { threshold });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return { ref, inView };
}

export default function ContactSection() {
  const { ref: sectionRef, inView } = useInView(0.1);
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [charCount, setCharCount] = useState(0);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    if (name === 'message') {
      if (value.length > 500) return;
      setCharCount(value.length);
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) return;
    setSubmitted(true);
  };

  return (
    <section
      id="contact"
      ref={sectionRef}
      className="relative py-20 md:py-32 bg-biodata-dark overflow-hidden"
    >
      <div className="absolute inset-0 bg-gradient-radial from-biodata-teal/5 via-transparent to-transparent pointer-events-none" />

      <div className="relative z-10 w-full px-4 md:px-8 lg:px-12 max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          {/* Left: Info */}
          <div
            className={`transition-all duration-700 ${
              inView ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-6'
            }`}
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-biodata-cyan/10 border border-biodata-cyan/20 mb-4">
              <span className="text-xs text-biodata-cyan font-medium uppercase tracking-wider">Contacto</span>
            </div>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
              Hagamos que tu <span className="text-gradient">ciencia avance</span>
            </h2>
            <p className="text-biodata-muted text-base md:text-lg leading-relaxed mb-10">
              Estamos listos para colaborar en tu próximo proyecto. Cuéntanos sobre tus 
              necesidades y te contactaremos en menos de 24 horas.
            </p>

            {/* Contact Info */}
            <div className="space-y-6 mb-10">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-biodata-teal/10 flex items-center justify-center flex-shrink-0">
                  <i className="ri-mail-line text-biodata-teal" />
                </div>
                <div>
                  <div className="text-sm text-biodata-muted mb-1">Email</div>
                  <a href="mailto:contacto@analitica.mx" className="text-white hover:text-biodata-teal transition-colors">
                    contacto@analitica.mx
                  </a>
                </div>
              </div>


              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-biodata-cyan/10 flex items-center justify-center flex-shrink-0">
                  <i className="ri-map-pin-line text-biodata-cyan" />
                </div>
                <div>
                  <div className="text-sm text-biodata-muted mb-1">Ubicación</div>
                  <span className="text-white">Ciudad de México, México</span>
                </div>
              </div>
            </div>

            {/* Socials */}
            <div>
              <div className="text-sm text-biodata-muted mb-4">Síguenos</div>
              <div className="flex gap-3">
                {['ri-linkedin-fill', 'ri-twitter-x-fill', 'ri-instagram-line', 'ri-youtube-line'].map((icon) => (
                  <a
                    key={icon}
                    href="#"
                    onClick={(e) => e.preventDefault()}
                    className="w-10 h-10 rounded-lg bg-biodata-card border border-biodata-teal/10 flex items-center justify-center text-biodata-muted hover:text-biodata-teal hover:border-biodata-teal/30 transition-all"
                  >
                    <i className={icon} />
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Right: Form */}
          <div
            className={`transition-all duration-700 delay-200 ${
              inView ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-6'
            }`}
          >
            <div className="glass-card rounded-2xl p-6 md:p-8">
              {submitted ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 rounded-full bg-biodata-emerald/20 flex items-center justify-center mx-auto mb-4">
                    <i className="ri-check-line text-3xl text-biodata-emerald" />
                  </div>
                  <h3 className="text-xl font-display font-semibold text-white mb-2">
                    ¡Mensaje enviado!
                  </h3>
                  <p className="text-biodata-muted">
                    Te contactaremos pronto. Gracias por confiar en ANALITICA.
                  </p>
                </div>
              ) : (
                <form
                  data-readdy-form
                  action="https://readdy.ai/api/form/d86001rgv8igf6sht2fg"
                  method="POST"
                  onSubmit={handleSubmit}
                  className="space-y-5"
                >
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="name" className="block text-sm text-biodata-muted mb-2">
                        Nombre <span className="text-biodata-teal">*</span>
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 rounded-lg bg-biodata-dark/60 border border-biodata-teal/10 text-white text-sm placeholder:text-biodata-muted/50 focus:outline-none focus:border-biodata-teal/40 focus:ring-1 focus:ring-biodata-teal/20 transition-all"
                        placeholder="Tu nombre"
                      />
                    </div>
                    <div>
                      <label htmlFor="company" className="block text-sm text-biodata-muted mb-2">
                        Empresa
                      </label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-lg bg-biodata-dark/60 border border-biodata-teal/10 text-white text-sm placeholder:text-biodata-muted/50 focus:outline-none focus:border-biodata-teal/40 focus:ring-1 focus:ring-biodata-teal/20 transition-all"
                        placeholder="Nombre de tu empresa"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm text-biodata-muted mb-2">
                      Email <span className="text-biodata-teal">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-lg bg-biodata-dark/60 border border-biodata-teal/10 text-white text-sm placeholder:text-biodata-muted/50 focus:outline-none focus:border-biodata-teal/40 focus:ring-1 focus:ring-biodata-teal/20 transition-all"
                      placeholder="tu@email.com"
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm text-biodata-muted mb-2">
                      Mensaje <span className="text-biodata-teal">*</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={4}
                      maxLength={500}
                      className="w-full px-4 py-3 rounded-lg bg-biodata-dark/60 border border-biodata-teal/10 text-white text-sm placeholder:text-biodata-muted/50 focus:outline-none focus:border-biodata-teal/40 focus:ring-1 focus:ring-biodata-teal/20 transition-all resize-none"
                      placeholder="Cuéntanos sobre tu proyecto..."
                    />
                    <div className="text-right text-xs text-biodata-muted mt-1">
                      {charCount}/500
                    </div>
                  </div>

                  <button
                    type="submit"
                    className="w-full flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-biodata-teal text-white font-medium hover:bg-biodata-teal/90 transition-all duration-300 shadow-lg shadow-biodata-teal/25 whitespace-nowrap"
                  >
                    <span>Enviar mensaje</span>
                    <i className="ri-send-plane-line" />
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}